import { useParams, Link } from 'react-router-dom';
import PageMeta from '@/components/feature/PageMeta';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ArticleCard from '@/components/feature/ArticleCard';
import { getArticleBySlug, getRelatedArticles } from '@/mocks/articles';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = getArticleBySlug(slug || '');
  const relatedArticles = article ? getRelatedArticles(article, 4) : [];

  if (!article) {
    return (
      <>
        <ScrollToTop />
        <PageMeta
          title="文章未找到 - 黑料中文网"
          description="抱歉，您访问的文章不存在或已被删除。请查看其他娱乐爆料内容。"
          keywords="黑料中文网,文章未找到,娱乐爆料"
          canonicalUrl={`https://fuxiaoxiao.cn/article/${slug}`}
        />
        <div className="min-h-screen flex items-center justify-center pt-16">
          <div className="text-center">
            <div className="w-20 h-20 flex items-center justify-center mx-auto mb-6 rounded-full bg-background-100">
              <i className="ri-file-unknow-line text-3xl text-foreground-300"></i>
            </div>
            <h1 className="text-2xl font-heading font-bold text-foreground-900 mb-3">文章未找到</h1>
            <p className="text-foreground-500 mb-6">抱歉，您访问的文章不存在或已被删除</p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary-500 hover:bg-primary-600 text-background-50 font-medium text-sm whitespace-nowrap cursor-pointer"
            >
              返回首页
            </Link>
          </div>
        </div>
      </>
    );
  }

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    image: article.coverImage,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    datePublished: article.publishDate,
    publisher: {
      '@type': 'Organization',
      name: '黑料中文网',
      logo: {
        '@type': 'ImageObject',
        url: 'https://fuxiaoxiao.cn/logo.png',
      },
    },
  };

  return (
    <>
      <ScrollToTop />
      <PageMeta
        title={`${article.title} - 黑料中文网`}
        description={article.excerpt}
        keywords={`${article.tags.join(',')},黑料中文网,娱乐爆料,${article.category}`}
        canonicalUrl={`https://fuxiaoxiao.cn/article/${article.slug}`}
        ogType="article"
        ogImage={article.coverImage}
        schema={articleSchema}
      />

      <main className="pt-20 pb-14 bg-background-50">
        <article className="w-full px-4 md:px-6 max-w-4xl mx-auto">
          <div className="mb-6">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: article.category, href: `/category/${article.categorySlug}` },
                { label: article.title },
              ]}
            />
          </div>

          <header className="mb-8">
            <div className="flex items-center gap-3 mb-4 flex-wrap">
              <Link
                to={`/category/${article.categorySlug}`}
                className="px-3 py-1 rounded-full text-xs font-medium bg-primary-100 text-primary-700 hover:bg-primary-200 transition-colors whitespace-nowrap cursor-pointer"
              >
                {article.category}
              </Link>
              {article.isHot && (
                <span className="px-3 py-1 rounded-full text-xs font-medium bg-accent-100 text-accent-700">
                  热门
                </span>
              )}
            </div>

            <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-foreground-900 leading-tight mb-4">
              {article.title}
            </h1>

            <div className="flex items-center gap-4 text-sm text-foreground-500 flex-wrap">
              <span className="flex items-center gap-1.5">
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-user-line"></i>
                </div>
                {article.author}
              </span>
              <span className="flex items-center gap-1.5">
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-calendar-line"></i>
                </div>
                {article.publishDate}
              </span>
              <span className="flex items-center gap-1.5">
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-time-line"></i>
                </div>
                阅读约{article.readTime}分钟
              </span>
              <span className="flex items-center gap-1.5">
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-eye-line"></i>
                </div>
                {article.viewCount >= 10000 ? `${(article.viewCount / 10000).toFixed(1)}万` : article.viewCount} 次阅读
              </span>
            </div>
          </header>

          <div className="mb-10 rounded-lg overflow-hidden aspect-[16/9]">
            <img
              src={article.coverImage}
              alt={article.title}
              className="w-full h-full object-cover"
            />
          </div>

          <div
            className="prose prose-lg max-w-none mb-10 text-foreground-800 leading-relaxed article-content"
            dangerouslySetInnerHTML={{ __html: article.content }}
          />

          <div className="border-t border-background-200/70 pt-6 mb-10">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-sm text-foreground-500">标签：</span>
              {article.tags.map((tag) => (
                <span
                  key={tag}
                  className="px-3 py-1 rounded-full text-xs bg-background-100 text-foreground-600 border border-background-200/70"
                >
                  #{tag}
                </span>
              ))}
            </div>
          </div>

          <div className="flex items-center justify-between py-6 border-t border-b border-background-200/70 mb-10">
            <div className="flex items-center gap-3">
              <span className="text-sm text-foreground-500">分享到：</span>
              <button className="w-9 h-9 flex items-center justify-center rounded-full bg-background-100 text-foreground-500 hover:bg-primary-100 hover:text-primary-600 transition-colors cursor-pointer" aria-label="分享到微博">
                <i className="ri-weibo-line"></i>
              </button>
              <button className="w-9 h-9 flex items-center justify-center rounded-full bg-background-100 text-foreground-500 hover:bg-primary-100 hover:text-primary-600 transition-colors cursor-pointer" aria-label="分享到微信">
                <i className="ri-wechat-line"></i>
              </button>
              <button className="w-9 h-9 flex items-center justify-center rounded-full bg-background-100 text-foreground-500 hover:bg-primary-100 hover:text-primary-600 transition-colors cursor-pointer" aria-label="复制链接">
                <i className="ri-link"></i>
              </button>
            </div>
          </div>
        </article>

        {relatedArticles.length > 0 && (
          <section className="w-full px-4 md:px-6 max-w-7xl mx-auto mt-10">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-6">
              相关文章
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {relatedArticles.map((relArticle) => (
                <ArticleCard key={relArticle.id} article={relArticle} />
              ))}
            </div>
          </section>
        )}
      </main>

      <style>{`
        .article-content h2 {
          font-size: 1.5rem;
          font-weight: 700;
          margin-top: 2rem;
          margin-bottom: 1rem;
          color: oklch(var(--foreground-900));
          font-family: var(--font-heading);
        }
        .article-content p {
          margin-bottom: 1.25rem;
          line-height: 1.85;
          color: oklch(var(--foreground-700));
          font-size: 1.05rem;
        }
      `}</style>
    </>
  );
}